import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import { DatePicker } from '@shared-ui/components';
import type { SubscriptionDefinition } from '../../types/settings';
import { QUESTION_SUBJECT_OPTIONS, SUBJECT_NAMES, titleCaseSubjectKey, PLAN_TIERS, type PlanTier, type ResetPeriod } from '@padcanvas/shared';
import { Eligibility } from './Eligibility';
import './Subscriptions.css';
import './Billing.css';

const RESET_PERIODS: ResetPeriod[] = ['none', 'day', 'week', 'month', 'plan'];
const RESET_PERIOD_LABEL: Record<ResetPeriod, string> = {
  none: 'none', day: 'day', week: 'week', month: 'month', plan: 'Plan month (from purchase)',
};

/** Rupees as typed → paise; '' when the field is empty. */
const toPaise = (rupees: string): number | '' => (rupees.trim() === '' ? '' : Math.round(Number(rupees) * 100));
const toRupees = (paise: number | undefined): string => (paise === undefined ? '' : String(paise / 100));

/**
 * The billing fields of a plan (docs/FeatureDesigns/billing/subscriptions-and-payments-target.md §2):
 * tier, purchasable, monthly prices (stored in paise, entered in rupees) and the
 * device limit. A field cleared here is removed from the definition, not saved empty.
 */
function PlanBillingFields({ sub, onChange }: {
  sub: SubscriptionDefinition;
  onChange: (next: SubscriptionDefinition) => void;
}) {
  const without = <K extends keyof SubscriptionDefinition>(key: K): SubscriptionDefinition => {
    const next = { ...sub };
    delete next[key];
    return next;
  };

  const setPrice = (field: 'student' | 'teacherPerStudent', rupees: string) => {
    const paise = toPaise(rupees);
    const prices = { student: sub.pricePaise?.student, teacherPerStudent: sub.pricePaise?.teacherPerStudent, [field]: paise === '' ? undefined : paise };
    if (prices.student === undefined && prices.teacherPerStudent === undefined) {
      onChange(without('pricePaise'));
      return;
    }
    onChange({ ...sub, pricePaise: { student: prices.student ?? 0, teacherPerStudent: prices.teacherPerStudent ?? 0 } });
  };

  return (
    <div className="sub-def-edit-fields sub-def-billing-fields" onClick={e => e.stopPropagation()}>
      <label>
        Tier
        <select
          value={sub.tier ?? ''}
          onChange={e => {
            const val = e.target.value as PlanTier | '';
            onChange(val === '' ? without('tier') : { ...sub, tier: val });
          }}
        >
          <option value="">none (legacy plan)</option>
          {PLAN_TIERS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <label>
        Student ₹ / month
        <input
          type="number" min={0} step="0.01"
          value={toRupees(sub.pricePaise?.student)}
          onChange={e => setPrice('student', e.target.value)}
          placeholder="—"
        />
      </label>
      <label>
        Teacher ₹ / student / month
        <input
          type="number" min={0} step="0.01"
          value={toRupees(sub.pricePaise?.teacherPerStudent)}
          onChange={e => setPrice('teacherPerStudent', e.target.value)}
          placeholder="—"
        />
      </label>
      <label>
        Device limit
        <input
          type="number" min={1} step={1}
          value={sub.deviceLimit ?? ''}
          onChange={e => {
            const val = e.target.value.trim();
            onChange(val === '' ? without('deviceLimit') : { ...sub, deviceLimit: Math.max(1, Math.round(Number(val))) });
          }}
          placeholder="no limit"
        />
      </label>
      <label className="sub-def-check">
        <input
          type="checkbox"
          checked={!!sub.purchasable}
          onChange={e => onChange(e.target.checked ? { ...sub, purchasable: true } : without('purchasable'))}
        />
        Purchasable
      </label>
    </div>
  );
}

// ============================================================================
// Tab 1: Definitions
// ============================================================================

function DefinitionsTab() {
  const queryClient = useQueryClient();
  const [editingSubs, setEditingSubs] = useState<SubscriptionDefinition[] | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ['subscriptionDefinitions'],
    queryFn: adminApi.getSubscriptionDefinitions,
  });

  const { data: eligibilityDefs } = useQuery({
    queryKey: ['eligibilityDefinitions'],
    queryFn: adminApi.getEligibilityDefinitions,
  });

  const subs = editingSubs ?? subscriptions ?? [];

  const saveMutation = useMutation({
    mutationFn: (data: SubscriptionDefinition[]) => adminApi.updateSubscriptionDefinitions(data),
    onMutate: () => setSaveStatus('saving'),
    onSuccess: () => {
      setSaveStatus('saved');
      setEditingSubs(null);
      queryClient.invalidateQueries({ queryKey: ['subscriptionDefinitions'] });
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: () => {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
  });

  const handleAddSubscription = () => {
    if (!newId.trim() || !newName.trim()) return;
    const now = new Date().toISOString();
    const newSub: SubscriptionDefinition = {
      id: newId.trim().toLowerCase().replace(/\s+/g, '-'),
      name: newName.trim(),
      description: newDescription.trim(),
      eligibilities: {},
      createdAt: now,
      updatedAt: now,
    };
    setEditingSubs([...subs, newSub]);
    setNewId('');
    setNewName('');
    setNewDescription('');
    setShowAddForm(false);
    setExpandedId(newSub.id);
  };

  const handleRemoveSubscription = (id: string) => {
    if (!window.confirm('Remove this subscription definition?')) return;
    setEditingSubs(subs.filter(s => s.id !== id));
  };

  const replaceSub = (next: SubscriptionDefinition) => {
    setEditingSubs(subs.map(s => (s.id === next.id ? next : s)));
  };

  const handleResetPeriodChange = (subId: string, featureName: string, value: ResetPeriod | null) => {
    setEditingSubs(
      subs.map(s => {
        if (s.id !== subId) return s;
        const resetPeriods = { ...(s.resetPeriods ?? {}) };
        if (value === null) {
          delete resetPeriods[featureName];
        } else {
          resetPeriods[featureName] = value;
        }
        const next: SubscriptionDefinition = { ...s, resetPeriods };
        if (Object.keys(resetPeriods).length === 0) delete next.resetPeriods;
        return next;
      })
    );
  };

  const handleEligibilityChange = (subId: string, featureName: string, value: string | null) => {
    setEditingSubs(
      subs.map(s => {
        if (s.id !== subId) return s;
        const eligibilities = { ...s.eligibilities };
        if (value === null) {
          delete eligibilities[featureName];
        } else {
          eligibilities[featureName] = value;
        }
        return { ...s, eligibilities };
      })
    );
  };

  if (isLoading) return <div className="loading">Loading...</div>;

  return (
    <div className="subscriptions-definitions">
      {subs.length === 0 && !showAddForm ? (
        <p className="no-features-message">No subscription definitions yet.</p>
      ) : (
        <div className="sub-def-list">
          {subs.map(sub => (
            <div key={sub.id} className="sub-def-card">
              <div
                className="sub-def-header"
                onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
              >
                <div className="sub-def-header-left">
                  <span className="sub-def-name">{sub.name}</span>
                  <span className="sub-def-id">{sub.id}</span>
                  {sub.tier && <span className="sub-def-id">tier: {sub.tier}{sub.purchasable ? ' · purchasable' : ''}</span>}
                  {sub.description && <span className="sub-def-desc">{sub.description}</span>}
                </div>
                <div className="sub-def-header-right">
                  <span className="sub-def-eligibility-count">
                    {Object.keys(sub.eligibilities).length} eligibilities
                  </span>
                  <span className="expand-icon">{expandedId === sub.id ? '\u25B2' : '\u25BC'}</span>
                </div>
              </div>

              {expandedId === sub.id && eligibilityDefs && (
                <div className="sub-def-eligibilities">
                  <div className="sub-def-edit-fields">
                    <label>
                      Name
                      <input
                        type="text"
                        value={sub.name}
                        onClick={e => e.stopPropagation()}
                        onChange={e => {
                          const val = e.target.value;
                          setEditingSubs(subs.map(s => s.id === sub.id ? { ...s, name: val } : s));
                        }}
                      />
                    </label>
                    <label>
                      Description
                      <input
                        type="text"
                        value={sub.description || ''}
                        onClick={e => e.stopPropagation()}
                        onChange={e => {
                          const val = e.target.value;
                          setEditingSubs(subs.map(s => s.id === sub.id ? { ...s, description: val } : s));
                        }}
                        placeholder="Optional description"
                      />
                    </label>
                  </div>
                  <PlanBillingFields sub={sub} onChange={replaceSub} />
                  <div className="eligibility-header">
                    <span>Eligibility</span>
                    <span>Value · reset period</span>
                  </div>
                  {eligibilityDefs.map(feat => {
                    const currentVal = sub.eligibilities[feat.name];
                    const isSet = currentVal !== undefined;
                    return (
                      <div key={feat.name} className={`eligibility-row ${isSet ? 'set' : ''}`}>
                        <div className="eligibility-row-left">
                          <span className="eligibility-feature-name">{feat.displayName}</span>
                          <span className="eligibility-feature-id">{feat.name}</span>
                        </div>
                        <div className="eligibility-row-right">
                          <select
                            value={currentVal ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleEligibilityChange(sub.id, feat.name, val === '' ? null : val);
                            }}
                          >
                            <option value="">-- Not set --</option>
                            {feat.enableValues.map(v => (
                              <option key={v} value={v}>
                                {v}{v === feat.defaultValue ? ' (default)' : ''}
                              </option>
                            ))}
                          </select>
                          {(feat.resetPeriod !== undefined || feat.name.endsWith('_quota') || sub.resetPeriods?.[feat.name]) && (
                            <select
                              className="reset-period-select"
                              aria-label={`Reset period for ${feat.displayName}`}
                              title="Per-plan reset window; overrides the eligibility definition's"
                              value={sub.resetPeriods?.[feat.name] ?? ''}
                              onChange={(e) => {
                                const val = e.target.value as ResetPeriod | '';
                                handleResetPeriodChange(sub.id, feat.name, val === '' ? null : val);
                              }}
                            >
                              <option value="">use definition ({feat.resetPeriod ?? 'none'})</option>
                              {RESET_PERIODS.map(p => <option key={p} value={p}>{RESET_PERIOD_LABEL[p]}</option>)}
                            </select>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="sub-def-actions-inline">
                    <button className="remove-sub-btn" onClick={() => handleRemoveSubscription(sub.id)}>
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showAddForm ? (
        <div className="add-sub-form">
          <h3>Add Subscription</h3>
          <div className="add-sub-fields">
            <label>
              ID (slug)
              <input
                type="text"
                value={newId}
                onChange={(e) => setNewId(e.target.value)}
                placeholder="e.g. pro-plan"
              />
            </label>
            <label>
              Name
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Pro Plan"
              />
            </label>
            <label>
              Description
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Optional description"
              />
            </label>
          </div>
          <div className="add-sub-form-actions">
            <button className="save-features-btn" onClick={handleAddSubscription} disabled={!newId.trim() || !newName.trim()}>
              Add
            </button>
            <button className="reset-btn" onClick={() => setShowAddForm(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="add-sub-btn" onClick={() => setShowAddForm(true)}>
          + Add Subscription
        </button>
      )}

      <div className="features-actions" style={{ marginTop: 20 }}>
        <button
          className="save-features-btn"
          onClick={() => saveMutation.mutate(subs)}
          disabled={saveMutation.isPending || !editingSubs}
        >
          {saveMutation.isPending ? 'Saving...' : 'Save Definitions'}
        </button>
        {editingSubs && (
          <button className="reset-btn" onClick={() => setEditingSubs(null)}>
            Discard Changes
          </button>
        )}
        {saveStatus === 'saved' && <span className="status-saved">Saved!</span>}
        {saveStatus === 'error' && <span className="status-error">Failed to save</span>}
      </div>
    </div>
  );
}

// ============================================================================
// Tab 2: Assignments
// ============================================================================

function AssignmentsTab() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignSubId, setAssignSubId] = useState('');
  const [assignStartDate, setAssignStartDate] = useState('');
  const [assignEndDate, setAssignEndDate] = useState('');
  const [assignSubjects, setAssignSubjects] = useState<string[]>([]);

  const { data: usersData } = useQuery({
    queryKey: ['users', { pageSize: 100 }],
    queryFn: () => adminApi.getUsers({ pageSize: 100 }),
  });

  const { data: subscriptionDefs } = useQuery({
    queryKey: ['subscriptionDefinitions'],
    queryFn: adminApi.getSubscriptionDefinitions,
  });

  const { data: userSubs, isLoading: subsLoading } = useQuery({
    queryKey: ['userSubscriptions', selectedUserId],
    queryFn: () => adminApi.getUserSubscriptions(selectedUserId!),
    enabled: !!selectedUserId,
  });

  const assignMutation = useMutation({
    mutationFn: () =>
      adminApi.assignUserSubscription(selectedUserId!, {
        subscriptionId: assignSubId,
        startDate: assignStartDate,
        endDate: assignEndDate,
        subjects: assignSubjects,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSubscriptions', selectedUserId] });
      setShowAssignForm(false);
      setAssignSubId('');
      setAssignStartDate('');
      setAssignEndDate('');
      setAssignSubjects([]);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (subId: string) => adminApi.cancelUserSubscription(selectedUserId!, subId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSubscriptions', selectedUserId] });
    },
  });

  const filteredUsers = usersData?.items.filter(u => {
    if (!searchQuery.trim()) return false;
    const q = searchQuery.toLowerCase();
    return (
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.id.toLowerCase().includes(q)
    );
  }) ?? [];

  const selectedUser = usersData?.items.find(u => u.id === selectedUserId);

  return (
    <div className="subscriptions-assignments">
      {/* User Search */}
      <div className="user-search-section">
        <input
          type="text"
          className="user-search-input"
          placeholder="Search users by name, email, or ID..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!e.target.value.trim()) setSelectedUserId(null);
          }}
        />
        {searchQuery.trim() && filteredUsers.length > 0 && !selectedUserId && (
          <div className="user-search-results">
            {filteredUsers.slice(0, 10).map(u => (
              <div
                key={u.id}
                className="user-search-result-item"
                onClick={() => {
                  setSelectedUserId(u.id);
                  setSearchQuery(u.name);
                }}
              >
                <span className="user-result-name">{u.name}</span>
                <span className="user-result-email">{u.email}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected User's Subscriptions */}
      {selectedUser && (
        <div className="user-subs-section">
          <div className="user-subs-header">
            <h3>{selectedUser.name}</h3>
            <span className="user-subs-email">{selectedUser.email}</span>
          </div>

          {subsLoading ? (
            <div className="loading">Loading subscriptions...</div>
          ) : !userSubs || userSubs.length === 0 ? (
            <p className="no-features-message">No subscriptions assigned.</p>
          ) : (
            <table className="dt dt--compact">
              <thead>
                <tr>
                  <th>Subscription</th>
                  <th>Subjects</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {userSubs.map(sub => {
                  const isExpired = new Date(sub.endDate) < new Date();
                  const isActive = sub.status === 'active' && !isExpired;
                  return (
                    <tr key={sub.id} className={!isActive ? 'dt-row--disabled' : ''}>
                      <td>
                        <strong>{sub.subscriptionName}</strong>
                        <br />
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{sub.subscriptionId}</span>
                      </td>
                      <td>{sub.subjects?.length ? sub.subjects.map(s => SUBJECT_NAMES[s] || titleCaseSubjectKey(s)).join(', ') : 'All'}</td>
                      <td>{new Date(sub.startDate).toLocaleDateString()}</td>
                      <td>{new Date(sub.endDate).toLocaleDateString()}</td>
                      <td>
                        {sub.status === 'cancelled' ? (
                          <span className="sub-status-badge cancelled">Cancelled</span>
                        ) : isExpired ? (
                          <span className="sub-status-badge expired">Expired</span>
                        ) : (
                          <span className="sub-status-badge active">Active</span>
                        )}
                      </td>
                      <td>
                        {isActive && (
                          <button
                            className="cancel-sub-btn"
                            onClick={() => {
                              if (window.confirm(`Cancel subscription "${sub.subscriptionName}"?`)) {
                                cancelMutation.mutate(sub.id);
                              }
                            }}
                            disabled={cancelMutation.isPending}
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Assign Form */}
          {showAssignForm ? (
            <div className="assign-form">
              <h4>Assign Subscription</h4>
              <div className="assign-form-fields">
                <label>
                  Subscription
                  <select value={assignSubId} onChange={(e) => setAssignSubId(e.target.value)}>
                    <option value="">Select...</option>
                    {(subscriptionDefs ?? []).map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Start Date
                  <DatePicker
                    value={assignStartDate}
                    onChange={setAssignStartDate}
                    ariaLabel="Start date"
                  />
                </label>
                <label>
                  End Date
                  <DatePicker
                    value={assignEndDate}
                    onChange={setAssignEndDate}
                    ariaLabel="End date"
                  />
                </label>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Subjects <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(leave empty for all)</span>
                </label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {QUESTION_SUBJECT_OPTIONS.map(s => (
                    <label key={s} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: 'var(--text-primary)' }}>
                      <input
                        type="checkbox"
                        checked={assignSubjects.includes(s)}
                        onChange={() => setAssignSubjects(prev =>
                          prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
                        )}
                      />
                      {SUBJECT_NAMES[s] || titleCaseSubjectKey(s)}
                    </label>
                  ))}
                </div>
              </div>
              <div className="assign-form-actions">
                <button
                  className="save-features-btn"
                  onClick={() => assignMutation.mutate()}
                  disabled={!assignSubId || !assignStartDate || !assignEndDate || assignMutation.isPending}
                >
                  {assignMutation.isPending ? 'Assigning...' : 'Assign'}
                </button>
                <button className="reset-btn" onClick={() => setShowAssignForm(false)}>Cancel</button>
                {assignMutation.isError && (
                  <span className="status-error">{(assignMutation.error as Error).message}</span>
                )}
              </div>
            </div>
          ) : (
            <button className="add-sub-btn" onClick={() => setShowAssignForm(true)} style={{ marginTop: 16 }}>
              + Assign Subscription
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function Subscriptions() {
  const [activeTab, setActiveTab] = useState<'definitions' | 'assignments' | 'eligibilities'>('definitions');

  return (
    <div className="subscriptions-page">
      <h1>Subscriptions</h1>

      <div className="user-detail-tabs">
        <button
          className={`user-detail-tab ${activeTab === 'definitions' ? 'active' : ''}`}
          onClick={() => setActiveTab('definitions')}
        >
          Definitions
        </button>
        <button
          className={`user-detail-tab ${activeTab === 'assignments' ? 'active' : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
          Assignments
        </button>
        <button
          className={`user-detail-tab ${activeTab === 'eligibilities' ? 'active' : ''}`}
          onClick={() => setActiveTab('eligibilities')}
        >
          Eligibilities
        </button>
      </div>

      <div className="features-section">
        {activeTab === 'definitions' && <DefinitionsTab />}
        {activeTab === 'assignments' && <AssignmentsTab />}
        {activeTab === 'eligibilities' && <Eligibility />}
      </div>
    </div>
  );
}
